import { listRetrievalReview } from './retrievalReviewApi'
import type { RetrievalReviewItem } from './retrievalReviewApi'

/**
 * Module-level single-flight cache for retrieval records, so the "record peek"
 * disclosures scattered across the triage/reaction decks share one fetch. The
 * `listRetrievalReview('all')` callable returns every record (embeddings
 * stripped) in one shot, so the first peek to expand pays for the whole set and
 * every later peek — for any gif — is instant.
 *
 * This is NOT eager-fetched on page load: the promise is created only on the
 * first call to {@link getRetrievalRecordFor}. A failed fetch clears the cached
 * promise so a later peek can retry rather than being stuck on the stale error.
 */
let recordsPromise: Promise<Map<string, RetrievalReviewItem>> | null = null

/**
 * A cache miss may just mean the library changed after this tab built the map
 * (e.g. a sync:pull added records). One re-fetch per quiet period self-heals
 * that; the cooldown keeps repeated peeks at a genuinely-absent id from
 * hammering the callable.
 */
const MISS_REFETCH_COOLDOWN_MS = 30_000
let lastMissRefetchAt = 0

function loadAllRecords(): Promise<Map<string, RetrievalReviewItem>> {
  if (!recordsPromise) {
    recordsPromise = listRetrievalReview('all')
      .then(({ items }) => {
        const byId = new Map<string, RetrievalReviewItem>()
        for (const item of items) byId.set(item.gifId, item)
        return byId
      })
      .catch((err) => {
        // Drop the rejected promise so the next peek re-fetches instead of
        // resolving to the same error forever.
        recordsPromise = null
        throw err
      })
  }
  return recordsPromise
}

/**
 * Resolve the retrieval record for a single gif, sharing one underlying fetch
 * across all callers. A miss triggers one cooldown-limited re-fetch in case the
 * library grew since the map was built. Returns `null` when the gif truly has
 * no retrieval record.
 */
export async function getRetrievalRecordFor(
  gifId: string
): Promise<RetrievalReviewItem | null> {
  const byId = await loadAllRecords()
  const hit = byId.get(gifId)
  if (hit) return hit
  const now = Date.now()
  if (now - lastMissRefetchAt < MISS_REFETCH_COOLDOWN_MS) return null
  lastMissRefetchAt = now
  recordsPromise = null
  const fresh = await loadAllRecords()
  return fresh.get(gifId) ?? null
}
